import { Alert } from '@creditornot/cb-alert';
import { Input } from '@creditornot/cb-input';
import { Select } from '@creditornot/cb-select';
import { useQuery } from '@tanstack/react-query';
import React, { useCallback, useMemo, useState } from 'react';
import { Field, useForm } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { RFFFieldErrorWrapper } from 'common/components';

import { getCountryConfigs } from './api';
import { useLanguage } from '../../../../locale';

const VenueFields = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  column-gap: 10px;
  row-gap: 16px;
`;

const CityAndPostalCode = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 10px;
`;

const WarningAlert = styled(Alert).attrs({
  variant: 'warning',
  size: 'small',
})``;

const VenueDetails = ({ country }: { country: string | null }) => {
  const intl = useIntl();
  const language = useLanguage();
  const form = useForm();
  const [selectedCity, setSelectedCity] = useState<string | null>('');
  const t = useCallback((id: string) => intl.formatMessage({ id }), [intl]);

  const { data: countryConfigs } = useQuery(['merchant-admin/config'], getCountryConfigs);

  const cityOptions = useMemo(
    () =>
      countryConfigs
        ?.find(config => config.code === country)
        ?.cities.map(city => {
          const cityNameInEnglish = city.name.find(n => n.lang === 'en')?.value || city.slug;
          const cityLabel = city.name.find(n => n.lang === language.key)?.value || cityNameInEnglish;

          return {
            label: cityLabel,
            value: cityNameInEnglish,
          };
        }) || [],
    [countryConfigs, country, language.key],
  );

  const renderCityOptions = [
    ...cityOptions,
    {
      label: t('self-serve-sign-up.form-labels.venues.other-city'),
      value: 'other',
    },
  ];

  const vanueNumberOptions = [
    { label: t('self-serve-sign-up.form-labels.venues.venue-one'), value: 'one' },
    { label: t('self-serve-sign-up.form-labels.venues.venue-multiple'), value: 'multiple' },
  ];

  return (
    <VenueFields>
      <Field<string | null> name="venues.venueNumber">
        {field => (
          <RFFFieldErrorWrapper field={field}>
            {input => (
              <Select
                options={vanueNumberOptions}
                placeholder={t('self-serve-sign-up.form-labels.venues.venueNumber')}
                {...input}
              />
            )}
          </RFFFieldErrorWrapper>
        )}
      </Field>
      <Field<string | undefined> name="venues.name">
        {field => (
          <RFFFieldErrorWrapper field={field}>
            {input => <Input {...input} placeholder={t('self-serve-sign-up.form-labels.venues.storeName')} />}
          </RFFFieldErrorWrapper>
        )}
      </Field>

      <Field<string | undefined> name="venues.address">
        {field => (
          <RFFFieldErrorWrapper field={field}>
            {input => <Input {...input} placeholder={t('self-serve-sign-up.form-labels.venues.address')} />}
          </RFFFieldErrorWrapper>
        )}
      </Field>

      <CityAndPostalCode>
        <Field<string | undefined> name="venues.postCode">
          {field => (
            <RFFFieldErrorWrapper field={field}>
              {input => <Input {...input} placeholder={t('self-serve-sign-up.form-labels.venues.postalCode')} />}
            </RFFFieldErrorWrapper>
          )}
        </Field>
        <Field<string | null> name="venues.city">
          {field => (
            <RFFFieldErrorWrapper field={field}>
              {input => (
                <Select
                  inputId="venues.city"
                  options={renderCityOptions}
                  placeholder={t('self-serve-sign-up.form-labels.venues.city')}
                  {...input}
                  onChange={newValue => {
                    input.onChange(newValue);
                    setSelectedCity(newValue);
                    form.change('venues.isWoltCity', newValue !== 'other');
                  }}
                />
              )}
            </RFFFieldErrorWrapper>
          )}
        </Field>
      </CityAndPostalCode>

      {selectedCity === 'other' && (
        <>
          <Field<string | undefined> name="venues.otherCity">
            {field => (
              <RFFFieldErrorWrapper field={field}>
                {input => <Input {...input} placeholder={t('self-serve-sign-up.form-labels.venues.other-city')} />}
              </RFFFieldErrorWrapper>
            )}
          </Field>
          <WarningAlert variant="warning" size="small">
            <FormattedMessage id="self-serve-sign-up.form-labels.venues.other-city-warning" />
          </WarningAlert>
        </>
      )}
    </VenueFields>
  );
};

export default VenueDetails;
